import React from 'react';
import { Link } from 'react-router-dom';
import { X, Heart, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useWishlist } from '../contexts/WishlistContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Wishlist Drawer - Slide-out panel showing saved products
 */
const WishlistDrawer = ({ isOpen, onClose }) => {
  const { wishlist, removeFromWishlist, clearWishlist } = useWishlist();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500 fill-current" />
                <h2 className="text-lg font-semibold">My Wishlist</h2>
                <span className="bg-neutral-100 text-neutral-600 text-sm px-2 py-0.5 rounded-full">
                  {wishlist.length}
                </span>
              </div>
              <button 
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {wishlist.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <Heart className="w-16 h-16 text-neutral-200 mb-4" />
                  <p className="text-neutral-600 mb-2">Your wishlist is empty</p>
                  <p className="text-sm text-neutral-400 mb-6">
                    Save your favorite products to view them later
                  </p>
                  <Button onClick={onClose} variant="outline">
                    Continue Shopping
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {wishlist.map((product) => {
                    const imageUrl = product.image || '';
                    const fullImageUrl = imageUrl.startsWith('/') ? `${API_URL}${imageUrl}` : imageUrl;
                    const href = `/products/${product.product_type}`;

                    return (
                      <div key={product.id || product.product_type} className="p-4 flex gap-4">
                        {/* Image */}
                        <Link to={href} onClick={onClose} className="flex-shrink-0">
                          <div className="w-20 h-20 bg-neutral-100 rounded-lg overflow-hidden">
                            {fullImageUrl ? (
                              <img 
                                src={fullImageUrl}
                                alt={product.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs">
                                No image
                              </div>
                            )}
                          </div>
                        </Link>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <Link 
                            to={href} 
                            onClick={onClose}
                            className="font-medium text-neutral-900 hover:text-amber-600 transition-colors line-clamp-1"
                          >
                            {product.title}
                          </Link>
                          <p className="text-sm text-neutral-500 line-clamp-2 mt-1">
                            {product.description}
                          </p>
                          {product.price_from > 0 && (
                            <p className="text-amber-600 font-medium mt-1">
                              From ${product.price_from}
                            </p>
                          )}
                        </div>

                        {/* Remove button */}
                        <button
                          onClick={() => removeFromWishlist(product.product_type || product.id)}
                          className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          title="Remove from wishlist"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {wishlist.length > 0 && (
              <div className="p-4 border-t space-y-3">
                <Link to="/products" onClick={onClose}>
                  <Button className="w-full bg-amber-500 hover:bg-amber-600">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Shop Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={clearWishlist}
                >
                  Clear Wishlist
                </Button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

/**
 * Wishlist Button - Shows in header with count badge
 */
export const WishlistButton = ({ onClick }) => {
  const { wishlistCount } = useWishlist();

  return (
    <button
      onClick={onClick}
      className="relative p-2 hover:bg-neutral-100 rounded-full transition-colors"
      title="View Wishlist"
    >
      <Heart className={`w-5 h-5 ${wishlistCount > 0 ? 'text-red-500 fill-current' : 'text-neutral-600'}`} />
      {wishlistCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {wishlistCount > 9 ? '9+' : wishlistCount}
        </span>
      )}
    </button>
  );
};

export default WishlistDrawer;
